import store from '@/store'
import {
  connectionService,
  MIN_SPEED_THING_VEHICLE,
  MIN_SPEED_THING_FLEX,
  THING_HEALTH_CERTIFICATE_URI,
  RACE_TIMEOUT,
  THING_FIND_URI,
  THING_FETCH_URI,
  PERSON_FIND_URI
} from '@leapsight/styleguide'
import { i18n } from '@leapsight/portal'
import { deserializationThingsDB } from '@/api/indexDB/helpers'

export default {
  PAGINATION_VALUES: Object.freeze({
    PAGE: 1,
    LIMIT: 1000
  }),
  getThingId(thing) {
    const input = thing
    const fields = input.id.split(':')
    const thingId = fields[3]
    return thingId
  },
  getThingType(thing) {
    const input = thing
    const fields = input.type.split(':')
    const type = fields[2]
    return type
  },
  getGpsId(thing) {
    const hasGps = thing.hasOwnProperty('things')
    if (hasGps) {
      const index = thing.things[0].indexOf('-')
      const sliceGpsId = thing.things[0].slice(index + 1)
      return sliceGpsId
    } else {
      return ''
    }
  },
  getThingDomain(thing) {
    const type = this.getThingType(thing)
    let domain
    switch (type) {
      case 'vehicle':
        domain = thing.info.domain
        break
      case 'flex':
        domain = this.getGpsId(thing)
        break
      default:
        break
    }
    return domain
  },
  async callThingFromLocalDB({ entityApiDB }) {
    try {
      const thingsFilteredDB = await entityApiDB.list()

      const personId = localStorage.getItem('user_id') ?? ''
      const thingsFilteredDBByUser = thingsFilteredDB.filter(thingFilteredDB =>
        thingFilteredDB.personsId.some(pId => pId === personId)
      )

      for (const thing of thingsFilteredDBByUser) deserializationThingsDB(thing)

      return thingsFilteredDBByUser
    } catch {
      return []
    }
  },
  getThingStatus(type, data) {
    let status = ''
    if (type == 'vehicle') {
      const isContactOn = data.contact_on ?? data.ignitionStatus
      switch (isContactOn) {
        case true:
          data.speed > MIN_SPEED_THING_VEHICLE ? (status = 'moving') : ''
          data.speed <= MIN_SPEED_THING_VEHICLE ? (status = 'on') : ''
          break
        case false:
          data.speed > MIN_SPEED_THING_VEHICLE ? (status = 'tow') : ''
          data.speed <= MIN_SPEED_THING_VEHICLE ? (status = 'stop') : ''
          break
        default:
          data.speed > MIN_SPEED_THING_VEHICLE ? (status = 'moving') : ''
          data.speed <= MIN_SPEED_THING_VEHICLE ? (status = 'stop') : ''
          break
      }
    } else if (type == 'flex') {
      switch (true) {
        case data.speed > MIN_SPEED_THING_FLEX:
          //En movimiento
          status = 'moving'
          break
        case data.speed <= MIN_SPEED_THING_FLEX:
          //Detenido
          status = 'stop'
          break
      }
    }

    return status
  },
  getThingSubType(thing) {
    let subType = ''
    let icon = ''

    switch (thing) {
      case 'truck':
        subType = i18n.t('general.labels.things.subType.truck')
        icon = 'truck'
        break
      case 'road_machinery':
        subType = i18n.t('general.labels.things.subType.vial')
        icon = 'road_machinery'
        break
      case 'car':
        subType = i18n.t('general.labels.things.subType.car')
        icon = 'car'
        break
      case 'motorcycle':
        subType = i18n.t('general.labels.things.subType.motorcycle')
        icon = 'motorcycle'
        break
      case 'crane':
        subType = i18n.t('general.labels.things.subType.crane')
        icon = 'crane'
        break
      case 'heavy duty':
        subType = i18n.t('general.labels.things.subType.heavyDuty')
        icon = 'heavy_duty'
        break
      case 'van':
        subType = i18n.t('general.labels.things.subType.van')
        icon = 'van'
        break
      case 'flex':
        subType = i18n.t('general.labels.things.subType.flex')
        icon = 'bag'
        break
      case 'trailer':
        subType = i18n.t('general.labels.things.subType.trailer')
        icon = 'trailer'
        break
      case 'passenger':
        subType = i18n.t('general.labels.things.subType.passenger')
        icon = 'passenger'
        break
      default:
        // TODO --> cambiar el icono default de la tabla por algo generico
        subType = i18n.t('general.labels.things.subType.truck')
        icon = 'bag'
        break
    }
    return { subType, icon }
  },
  /**
   * @description Esta función retorna el ícono del thing
   * @param {*} marker Marker de google
   * @param {boolean} showEntityType Si esta en true busca otro set de íconos
   * @param {boolean} eventMarker En true devuelve el ícono genérico cuando la posición genero eventos
   * @returns icono del thing
   */
  getThingIcon(marker, showEntityType = false, eventMarker = false) {
    const status = marker.status
    const heading = marker.heading
    const type = marker.type
    const subType = marker.subType === 'trailer' ? 'car' : marker.subType
    let iconName
    //Si el paquete de datos tuviese algun conflicto con el estado o el rumbo no se muestra el marker
    if (status == 'events') {
      if (showEntityType) {
        iconName = `marker-${subType}-event-${heading}`
      } else {
        iconName = `marker-circle-event-${heading}`
      }
      const images = require.context('@/assets/trails', true, /\.png$/)
      return images(`./events/${iconName}.png`)
    } else if (status !== '' && heading !== '') {
      const folder = showEntityType
        ? `${type}/${subType}`
        : eventMarker
        ? 'events'
        : 'generic'
      switch (folder) {
        case 'generic':
          if (status == 'on' || status == 'stop')
            iconName = `marker-circle-${status}`
          else if (heading != '')
            // Los flex no envían rumbo por eso necesitamos saber si el heading viene vacío
            // para armar el nombre del ícono correctamente
            iconName = `marker-circle-${status}-${heading}`
          else iconName = `marker-circle-on`
          break
        case 'events':
          if (status == 'on' || status == 'stop') {
            iconName = `marker-circle-stop-event`
          } else {
            iconName = `marker-circle-event-${heading}`
          }
          break
        default:
          if (status == 'on' || status == 'stop')
            iconName = `marker-${subType}-${status}`
          else if (heading != '')
            iconName = `marker-${subType}-${status}-${heading}`
          else iconName = `marker-${subType}-${status}`
          break
      }
      const images = eventMarker
        ? require.context('@/assets/trails', true, /\.png$/)
        : require.context('@/assets/headings', true, /\.png$/)
      if (marker.follow) {
        iconName = iconName + '-follow'
      }
      return images(`./${folder}/${iconName}.png`)
    }
  },
  getThingHeading(heading) {
    if (typeof heading != 'number') {
      return ''
    }
    const cardinals = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
    const position =
      Math.round((heading %= 360) < 0 ? heading + 360 : heading / 45) % 8
    return cardinals[position]
  },
  getThingLabel(label) {
    if (label && label.length > 12) {
      const formatedLabel = label.slice(0, 10) + '...'
      return formatedLabel
    } else {
      return label
    }
  },
  getAccountId(thing) {
    const input = thing
    const fields = input.account_id.split(':')
    const accountId = fields[3]
    return accountId
  },
  getThingCertificate(arrayIds) {
    const connection = store.getters['authState/connection']
    const kwArgs = connectionService.createkwArgs()
    const options = {}

    const p1 = new Promise((resolve, reject) => {
      setTimeout(reject, RACE_TIMEOUT, false)
    })
    const p2 = new Promise((resolve, reject) => {
      connection.session
        .call(THING_HEALTH_CERTIFICATE_URI, arrayIds, kwArgs, options)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          if (
            err.error == 'com.magenta.error.thing_reported_state_not_updated'
          ) {
            reject('24hourlimit')
          } else reject(false)
        })
    })
    return Promise.race([p1, p2])
      .then(value => {
        return value
      })
      .catch(error => {
        return error
      })
  },

  // Se chequea el ID del thing, si esta en el array de vehiculos seguidos devuevle true para setear el marker correspondiente
  isFollowed(id) {
    const data = JSON.parse(localStorage.getItem('followedThings'))
    for (let i = 0; data && i < data.length; i++) {
      const element = data[i]
      if (element === id) {
        return true
      }
    }
    return false
  },
  serviceFuelCutOff(gpsId, fuelStatus) {
    return new Promise((resolve, reject) => {
      const connection = store.getters['authState/connection']
      const args = [gpsId, `fuel_cut_off:${fuelStatus}`]
      const kwArgs = connectionService.createkwArgs()
      const options = {}

      const p1 = new Promise((resolve, reject) => {
        setTimeout(resolve, RACE_TIMEOUT, { error: 'timeout1' })
      })
      const p2 = new Promise((resolve, reject) => {
        connection.session
          .call('com.magenta.thing.send', args, kwArgs, options)
          .then(
            response => {
              resolve(response)
            },
            wampError => {
              resolve(wampError)
            }
          )
      })
      Promise.race([p1, p2])
        .then(value => {
          resolve(value)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  getThingSpeedLastModified(thing) {
    const value = thing.reported_state.location
      ? thing.metadata.reported_state.speed.timestamp
      : 0
    return value
  },
  getThingServices(thing) {
    let services = {}
    if (thing.config.services) {
      services = thing.config.services
    }
    return services
  },
  getThingLastReport(thing) {
    const value = thing.reported_state.location
      ? thing.metadata.reported_state.location.timestamp
      : 0
    return value
  },
  getThingLatitude(thing) {
    const value = thing.reported_state.location
      ? thing.reported_state.location.coordinates[0]
      : 0

    return value
  },
  getThingLongitude(thing) {
    const value = thing.reported_state.location
      ? thing.reported_state.location.coordinates[1]
      : 0
    return value
  },
  fetchThing(thingId) {
    return new Promise((resolve, reject) => {
      const connection = store.getters['authState/connection']
      const args = [thingId]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security,
        query_spec: {
          _p:
            'id,' +
            'type,' +
            'metadata.reported_state.location,' +
            'metadata.reported_state.speed,' +
            'metadata.reported_state.signal_sensors,' +
            'reported_state.speed,' +
            'reported_state.location,' +
            'reported_state.heading,' +
            'reported_state.fuel_cut_off,' +
            'reported_state.signal_sensors,' +
            'reported_state.battery_level,' +
            'reported_state.contact_on,' +
            'reported_state.telemetry,' +
            'reported_state.ignition_status,' +
            'reported_state.ibutton,' +
            'is_alias'
        },
        most_recent: true
      }
      const p1 = new Promise(reject => {
        setTimeout(reject, RACE_TIMEOUT, { error: 'timeout' })
      })
      const p2 = new Promise((resolve, reject) => {
        connection.session.call(THING_FETCH_URI, args, kwArgs).then(
          response => {
            resolve(response)
          },
          wampError => {
            reject(wampError)
          }
        )
      })
      Promise.race([p1, p2])
        .then(value => {
          resolve(value)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  fetchThingServices(thingId) {
    return new Promise((resolve, reject) => {
      const connection = store.getters['authState/connection']
      const args = [thingId]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security,
        query_spec: {
          _p: 'config.services'
        }
      }
      const p1 = new Promise(reject => {
        setTimeout(reject, RACE_TIMEOUT, { error: 'timeout' })
      })
      const p2 = new Promise((resolve, reject) => {
        connection.session.call(THING_FETCH_URI, args, kwArgs).then(
          response => {
            resolve(response)
          },
          wampError => {
            reject(wampError)
          }
        )
      })
      Promise.race([p1, p2])
        .then(value => {
          resolve(value)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  getThingContacts(thingId) {
    return new Promise((resolve, reject) => {
      const connection = store.getters['authState/connection']
      const args = [thingId]
      const kwArgs = connectionService.createkwArgs()
      const options = {}

      const p1 = new Promise((resolve, reject) => {
        setTimeout(reject, RACE_TIMEOUT, { error: 'timeout' })
      })
      const p2 = new Promise((resolve, reject) => {
        connection.session
          .call('com.magenta.thing_contacts.fetch', args, kwArgs, options)
          .then(
            response => {
              resolve(response)
            },
            wampError => {
              reject(wampError)
            }
          )
      })
      Promise.race([p1, p2])
        .then(value => {
          resolve(value)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  getThingDriver(thingIbutton) {
    return new Promise((resolve, reject) => {
      const accountId = connectionService.getAccountId()
      const connection = store.getters['authState/connection']
      const args = [{ account_id: accountId, ibutton: thingIbutton }]
      const security = connectionService.createkwArgs()
      const kwArgs = {
        ...security,
        query_spec: {
          _p: 'first_name, last_name'
        }
      }
      const p1 = new Promise((resolve, reject) => {
        setTimeout(reject, RACE_TIMEOUT, { error: 'timeout' })
      })
      const p2 = new Promise((resolve, reject) => {
        connection.session
          .call(PERSON_FIND_URI, args, kwArgs, {})
          .then(
            response => {
              resolve(response)
            },
            wampError => {
              reject(wampError)
            }
          )
          .catch(err => {
            console.log(err)
          })
      })
      Promise.race([p1, p2])
        .then(value => {
          resolve(value)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  thingFind({ session, page, limit } = { session: null, page: null, limit: 200 }) {
    return new Promise((resolve, reject) => {
      const p1 = new Promise((resolve, reject) => {
        setTimeout(resolve, RACE_TIMEOUT, [])
      })
      const p2 = this.thingsListRace(session, page, limit)
      Promise.race([p1, p2])
        .then(value => {
          resolve(value)
        })
        .catch(error => {
          reject(error)
        })
    })
  },

  thingsListRace(session, page, limit) {
    return new Promise((resolve, reject) => {
      const args = [connectionService.getAccountId()]
      const kwArgs = connectionService.createkwArgs()
      const options = { timeout: 30000 }
      kwArgs.types = ['mrn:thing:vehicle', 'mrn:thing:flex']
      kwArgs.query_spec = {
        _p: 'account_id,' +
          'id,' +
          'type,' +
          'info,' +
          'config,' +
          'created_timestamp,' +
          'things,' +
          'metadata.reported_state.location,' +
          'metadata.reported_state.speed,' +
          'metadata.reported_state.signal_sensors,' +
          'reported_state.speed,' +
          'reported_state.location,' +
          'reported_state.heading,' +
          'reported_state.fuel_cut_off,' +
          'reported_state.signal_sensors,' +
          'reported_state.main_power_on,' +
          'reported_state.battery_level,' +
          'reported_state.contact_on,' +
          'reported_state.telemetry,' +
          'reported_state.ignition_status,' +
          'reported_state.ibutton,' +
          'is_alias,' +
          'aliases_qty',
        _page: page,
        _limit: limit
      }
      session.call(THING_FIND_URI, args, kwArgs, options).then(
        response => {
          resolve(response)
        },
        wampError => {
          reject(wampError)
        }
      )
    })
  },
  async subscribeThingsToBeUpdated({ things, updateCallback }) {
    const connection = store.getters['authState/connection']
    const filterThingSubscribePromises = []
    for (const thing of things) {
      const accountId = this.getAccountId(thing)
      const thingId = this.getThingId(thing)
      const thingType = this.getThingType(thing)
      const topic = `com.magenta.account.${accountId}.thing.${thingType}.${thingId}.updated`
      filterThingSubscribePromises.push(
        connection.session.subscribe(
          topic,
          args => {
            updateCallback(args)
          },
          { match: 'exact' }
        )
      )
    }
    try {
      const resThingsSubscribed = await Promise.allSettled(
        filterThingSubscribePromises
      )

      resThingsSubscribed.forEach(({ value: subscription }) => {
        if (Boolean(subscription)) {
          const payload = {
            type: 'thing',
            action: 'updated',
            subscription
          }
          store.dispatch('subscriptionState/addThingSubscription', payload)
        }
      })
    } catch (e) {
      console.error(e)
    }
  },
  //ERRORS
  getErrorMessageByCode(code) {
    let strToReturn
    switch (code) {
      case 'com.magenta.error.internal_error':
        strToReturn = "Ocurrió un error, por favor reintente en unos segundos."
        break

      case 'wamp.error.timeout':
        strToReturn = i18n.t('general.snackbarComponent.errors.timeout')
        break
      default:
        strToReturn = i18n.t(
          'general.snackbarComponent.errors.default_error_msje'
        )
        break
    }
    return strToReturn
  }
}
